import { useCallback, useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SearchFilterField, SearchSchema } from '../api/searchTypes';
import {
    useTraderSearch,
    type TraderSearchFailure,
} from '../api/useTraderSearch';
import { TraderExportBox } from '../export/TraderExportBox';
import { exclusiveToggle, listState, withParam } from '../state/searchParams';
import { buildTraderRequest, traderQuery } from '../state/traderRequest';
import { PaginationBar, type PagerCount } from './PaginationBar';
import { traderLabelStyles } from './TagChip';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { exportFieldsFor } from '../export/traderExport';
import { useTraderColumns } from '../state/useTraderColumns';
import { traderGridTemplate, traderMinWidth } from './traderColumns';
import { TraderResultsHeader } from './TraderResultsHeader';
import { TraderRow } from './TraderRow';

export const TRADER_PAGE_SIZES = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

export interface TraderResultsProps {
    schema: SearchSchema;
    spec: SearchFilterField[];
    params: URLSearchParams;
    defaults: Record<string, string>;
    onChange: (next: URLSearchParams) => void;
}

function failureText(
    failure: TraderSearchFailure,
    address: string | null | undefined
): string {
    if (failure === 'timeout') {
        return 'Took longer than 120 s. Narrow Active within or add a filter.';
    }
    if (failure === 'unknown_name' && address)
        return `No Polymarket profile named ${address}.`;
    return 'Could not load the traders. Try again in a moment.';
}

const TRADER_NOUN = { singular: 'trader', plural: 'traders' };

// The include and exclude URL lists behind the row chips.
const TAGS = ['tags_include', 'tags_exclude'] as const;
const CATS = ['main_category_include', 'main_category_exclude'] as const;

const NOTE_SX = {
    fontSize: '11px',
    color: 'text.disabled',
    px: 1.25,
    py: 0.75,
    // Stays in view when the table has been scrolled sideways.
    position: 'sticky',
    left: 0,
    width: 'fit-content',
} as const;

// The seconds counter is the only thing that changes every second while a
// search runs; the rows are memoised and do not follow it.
function SearchingNote({ seconds }: { seconds: number }) {
    return (
        <>
            <LinearProgress sx={{ height: 2 }} />
            <Typography sx={NOTE_SX}>
                {`Searching traders… ${seconds} s`}
            </Typography>
        </>
    );
}

export function TraderResults({
    schema,
    spec,
    params,
    defaults,
    onChange,
}: TraderResultsProps) {
    const sortOptions = useMemo(
        () => schema.sort_options.trader ?? [],
        [schema]
    );
    const request = useMemo(
        () => buildTraderRequest(params, spec, defaults, sortOptions),
        [params, spec, defaults, sortOptions]
    );
    const sizeParam = Number(params.get('limit'));
    const pageSize = TRADER_PAGE_SIZES.includes(sizeParam)
        ? sizeParam
        : DEFAULT_PAGE_SIZE;
    const search = useTraderSearch(request, pageSize);
    const { hint } = traderQuery(params.get('q') ?? '');
    // The visible columns follow the width of the scroll box (columns spec 4.6).
    const { ref: scrollRef, width } = useContainerWidth<HTMLDivElement>();
    const view = useTraderColumns(width);
    const gridTemplate = useMemo(
        () => traderGridTemplate(view.columns),
        [view.columns]
    );
    const minWidth = useMemo(
        () => traderMinWidth(view.columns),
        [view.columns]
    );
    const shownFields = useMemo(
        () => exportFieldsFor(view.columns),
        [view.columns]
    );
    const theme = useTheme();
    const paper = theme.palette.background.paper;
    const mode = theme.palette.mode;
    const labels = useMemo(
        () => traderLabelStyles(schema.trader_labels, paper, mode),
        [schema, paper, mode]
    );
    // "Last seen" is read against the time the rows arrived, not against
    // every render, so the memoised rows keep the same props between ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const nowMs = useMemo(() => Date.now(), [search.traders]);

    // The row chips toggle the same URL lists as the filter panel; include
    // and exclude of one value stay mutually exclusive. The rows are memoised,
    // so each of these is built once per URL and not on every render.
    const tagState = useCallback(
        (tag: string) => listState(params, ...TAGS, tag),
        [params]
    );
    const categoryState = useCallback(
        (category: string) => listState(params, ...CATS, category),
        [params]
    );
    const { onIncludeTag, onExcludeTag, onIncludeCategory, onExcludeCategory } =
        useMemo(() => {
            const toggle =
                (
                    keys: readonly [string, string],
                    target: 'include' | 'exclude'
                ) =>
                (value: string) =>
                    onChange(
                        exclusiveToggle(
                            params,
                            ...keys,
                            value,
                            target,
                            defaults
                        )
                    );
            return {
                onIncludeTag: toggle(TAGS, 'include'),
                onExcludeTag: toggle(TAGS, 'exclude'),
                onIncludeCategory: toggle(CATS, 'include'),
                onExcludeCategory: toggle(CATS, 'exclude'),
            };
        }, [onChange, params, defaults]);

    const sort = request.sort_by ?? '';
    const dir = request.sort_dir ?? 'desc';
    const onSort = (key: string) => {
        let next = withParam(params, 'sort', key, defaults);
        next =
            key !== sort
                ? withParam(next, 'dir', 'desc', defaults)
                : withParam(
                      next,
                      'dir',
                      dir === 'desc' ? 'asc' : 'desc',
                      defaults
                  );
        onChange(next);
    };

    const count: PagerCount =
        search.total !== null
            ? { kind: 'known', total: search.total }
            : search.countFailed
              ? { kind: 'failed' }
              : { kind: 'counting' };

    const dimmed = search.loading && search.traders.length > 0;

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                border: 1,
                borderColor: 'divider',
                borderRadius: '4px',
                overflow: 'hidden',
            }}
        >
            <Box
                ref={scrollRef}
                sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
            >
                <Box sx={{ minWidth }}>
                    <TraderResultsHeader
                        sort={sort}
                        dir={dir}
                        sortableKeys={sortOptions}
                        sortInfo={schema.sort_info}
                        onSort={onSort}
                        view={view}
                        gridTemplate={gridTemplate}
                    />

                    {search.loading && (
                        <SearchingNote seconds={search.elapsedSeconds} />
                    )}

                    {hint && <Typography sx={NOTE_SX}>{hint}</Typography>}

                    {search.failure && !search.loading && (
                        <Typography sx={NOTE_SX}>
                            {failureText(search.failure, request.address)}
                        </Typography>
                    )}

                    {!search.loading &&
                        !search.failure &&
                        search.traders.length === 0 && (
                            <Typography sx={NOTE_SX}>
                                Nothing matches. Loosen a filter or drop a word
                                from the query.
                            </Typography>
                        )}

                    <Box
                        data-testid="trader-rows"
                        sx={{
                            opacity: dimmed ? 0.45 : 1,
                            transition: 'opacity 120ms',
                            pointerEvents: dimmed ? 'none' : undefined,
                        }}
                    >
                        {search.traders.map(trader => (
                            <TraderRow
                                key={trader.address}
                                trader={trader}
                                nowMs={nowMs}
                                labels={labels}
                                tagState={tagState}
                                categoryState={categoryState}
                                onIncludeTag={onIncludeTag}
                                onExcludeTag={onExcludeTag}
                                onIncludeCategory={onIncludeCategory}
                                onExcludeCategory={onExcludeCategory}
                                columns={view.columns}
                                gridTemplate={gridTemplate}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>

            <PaginationBar
                pageIndex={search.pageIndex}
                pageSize={pageSize}
                hasPrev={search.hasPrev}
                hasNext={search.hasNext}
                busy={search.loading}
                pageSizes={TRADER_PAGE_SIZES}
                count={count}
                countNoun={TRADER_NOUN}
                onJump={search.goToPage}
                actions={
                    <TraderExportBox
                        request={request}
                        total={search.total}
                        counting={search.counting}
                        shownFields={shownFields}
                    />
                }
                onPrev={search.prevPage}
                onNext={search.nextPage}
                onPageSizeChange={size =>
                    onChange(
                        withParam(
                            params,
                            'limit',
                            size === DEFAULT_PAGE_SIZE ? null : String(size),
                            defaults
                        )
                    )
                }
            />
        </Box>
    );
}
